import { lazy } from 'react';
import type { StockSectionDef } from './pages/StockSectionPage';

// Pages and tabs load when first opened (NFR-04, docs §169).
const ItemsTab = lazy(() => import('./components/ItemsTab'));
const CategoriesTab = lazy(() => import('./components/MasterListsTab').then((m) => ({ default: m.CategoriesTab })));
const UnitsTab = lazy(() => import('./components/MasterListsTab').then((m) => ({ default: m.UnitsTab })));
const ItemChangesTab = lazy(() => import('./components/ItemChangesTab'));
const ItemImportTab = lazy(() => import('./components/ItemImportTab'));
const BinsTab = lazy(() => import('./components/StoresTab').then((m) => ({ default: m.BinsTab })));
const LabelsTab = lazy(() => import('./components/StoresTab').then((m) => ({ default: m.LabelsTab })));
const StoresListTab = lazy(() => import('./components/StoresTab').then((m) => ({ default: m.StoresListTab })));
const ReceiptsTab = lazy(() => import('./components/ReceivingTab').then((m) => ({ default: m.ReceiptsTab })));
const ReceivePendingTab = lazy(() => import('./components/ReceivingTab').then((m) => ({ default: m.ReceivePendingTab })));
const LandedCostsTab = lazy(() => import('./components/SupplierReturnsTab').then((m) => ({ default: m.LandedCostsTab })));
const SupplierReturnsTab = lazy(() => import('./components/SupplierReturnsTab').then((m) => ({ default: m.SupplierReturnsTab })));
const RequisitionsTab = lazy(() => import('./components/RequisitionsTab'));
const AwaitingTab = lazy(() => import('./components/VouchersTab').then((m) => ({ default: m.AwaitingTab })));
const IssueTab = lazy(() => import('./components/VouchersTab').then((m) => ({ default: m.IssueTab })));
const ReturnsTab = lazy(() => import('./components/VouchersTab').then((m) => ({ default: m.ReturnsTab })));
const TransfersTab = lazy(() => import('./components/TransfersTab'));
const DiscrepanciesTab = lazy(() => import('./components/TransfersTab').then((m) => ({ default: m.DiscrepanciesTab })));
const ControlledRegisterTab = lazy(() => import('./components/ControlledRegisterTab'));
const CountsListTab = lazy(() => import('./components/CountsTab').then((m) => ({ default: m.CountsListTab })));
const PostedCountsTab = lazy(() => import('./components/CountsTab').then((m) => ({ default: m.PostedCountsTab })));
const SchedulesTab = lazy(() => import('./components/CountsTab').then((m) => ({ default: m.SchedulesTab })));
const AdjustmentsListTab = lazy(() => import('./components/AdjustmentsTab').then((m) => ({ default: m.AdjustmentsListTab })));
const PostedAdjustmentsTab = lazy(() => import('./components/AdjustmentsTab').then((m) => ({ default: m.PostedAdjustmentsTab })));
const ReasonAccountsTab = lazy(() => import('./components/AdjustmentsTab').then((m) => ({ default: m.ReasonAccountsTab })));
const ReorderTab = lazy(() => import('./components/ReorderTab'));
const ForecastTab = lazy(() => import('./components/SupplyViews').then((m) => ({ default: m.ForecastTab })));
const ExpiringTab = lazy(() => import('./components/SupplyViews').then((m) => ({ default: m.ExpiringTab })));
const SupplierPerformanceTab = lazy(() => import('./components/SupplyViews').then((m) => ({ default: m.SupplierPerformanceTab })));
const TemperatureTab = lazy(() => import('./components/SupplyViews').then((m) => ({ default: m.TemperatureTab })));
const HoldingsTab = lazy(() => import('./components/RecallsTab').then((m) => ({ default: m.HoldingsTab })));
const RecallsListTab = lazy(() => import('./components/RecallsTab').then((m) => ({ default: m.RecallsListTab })));
const DestructionsTab = lazy(() => import('./components/RecallsTab').then((m) => ({ default: m.DestructionsTab })));
const StockReportsTab = lazy(() => import('./components/StockReportsTab'));
const BalancesTab = lazy(() => import('./components/BalancesTab'));
const SettingsAccountsTab = lazy(() => import('./components/StockSettingsTab').then((m) => ({ default: m.SettingsAccountsTab })));
const SettingsCloseTab = lazy(() => import('./components/StockSettingsTab').then((m) => ({ default: m.SettingsCloseTab })));
const SettingsGeneralTab = lazy(() => import('./components/StockSettingsTab').then((m) => ({ default: m.SettingsGeneralTab })));
const SettingsNumberingTab = lazy(() => import('./components/StockSettingsTab').then((m) => ({ default: m.SettingsNumberingTab })));

/**
 * The stock module's sections, one per menu item, each with its leaves. The URL's last segment names
 * the leaf, so every tab is a real address that can be shared, and the rights the leaf needs are
 * read once by the page and handed to the tab.
 */
export const stockSections = {
  items: {
    area: 'Master Data', title: 'Items', basePath: '/stock/master/items',
    description: 'The stock-keeping records: units, categories, barcodes, tracking flags and the costing method each item uses.',
    views: [
      { key: 'items', label: 'Items' },
      { key: 'categories', label: 'Categories' },
      { key: 'units', label: 'Units' },
      { key: 'changes', label: 'Change requests' },
      { key: 'import', label: 'Import' },
    ],
    render: (view, ctx) => {
      switch (view) {
        case 'categories': return <CategoriesTab ctx={ctx} />;
        case 'units': return <UnitsTab ctx={ctx} />;
        case 'changes': return <ItemChangesTab />;
        case 'import': return <ItemImportTab ctx={ctx} />;
        default: return <ItemsTab ctx={ctx} />;
      }
    },
  } satisfies StockSectionDef<'items' | 'categories' | 'units' | 'changes' | 'import'>,

  stores: {
    area: 'Master Data', title: 'Stores & Bins', basePath: '/stock/master/stores',
    description: 'Stores with a responsible officer, their zones and bins, and the labels that identify them.',
    views: [
      { key: 'stores', label: 'Stores' },
      { key: 'bins', label: 'Zones & bins' },
      { key: 'labels', label: 'Labels' },
    ],
    render: (view, ctx) => {
      switch (view) {
        case 'bins': return <BinsTab ctx={ctx} />;
        case 'labels': return <LabelsTab />;
        default: return <StoresListTab ctx={ctx} />;
      }
    },
  } satisfies StockSectionDef<'stores' | 'bins' | 'labels'>,

  receipts: {
    area: 'Receiving', title: 'Receipts', basePath: '/stock/receiving/receipts',
    description: 'Goods received against purchase orders, put away into bins, inspected where the item requires it.',
    views: [
      { key: 'receive', label: 'Receive against an order' },
      { key: 'pending', label: 'Pending put-away' },
      { key: 'inspection', label: 'Inspection' },
      { key: 'posted', label: 'Posted' },
    ],
    render: (view, ctx) => {
      switch (view) {
        case 'pending': return <ReceiptsTab ctx={ctx} status="PendingPutAway" />;
        case 'inspection': return <ReceiptsTab ctx={ctx} status="Inspection" />;
        case 'posted': return <ReceiptsTab ctx={ctx} status="Posted" />;
        default: return <ReceivePendingTab ctx={ctx} />;
      }
    },
  } satisfies StockSectionDef<'receive' | 'pending' | 'inspection' | 'posted'>,

  supplierReturns: {
    area: 'Receiving', title: 'Supplier Returns', basePath: '/stock/receiving/supplier-returns',
    description: 'Goods going back to the supplier, with the goods received note reduced and a credit note drafted.',
    views: [{ key: 'returns', label: 'Returns' }],
    render: (_view, ctx) => <SupplierReturnsTab ctx={ctx} />,
  } satisfies StockSectionDef<'returns'>,

  landedCosts: {
    area: 'Receiving', title: 'Landed Costs', basePath: '/stock/receiving/landed-costs',
    description: 'Freight, duty and clearing apportioned over the receipts they belong to.',
    views: [{ key: 'allocations', label: 'Allocations' }],
    render: (_view, ctx) => <LandedCostsTab ctx={ctx} />,
  } satisfies StockSectionDef<'allocations'>,

  requisitions: {
    area: 'Issues', title: 'Store Requisitions', basePath: '/stock/issues/requisitions',
    description: 'What departments ask the store for, checked against the budget and approved by the budget holder, and finance above the first tier.',
    views: [
      { key: 'mine', label: 'Mine' },
      { key: 'approve', label: 'To approve' },
      { key: 'approved', label: 'Approved' },
    ],
    render: (view, ctx) => <RequisitionsTab ctx={ctx} view={view} />,
  } satisfies StockSectionDef<'mine' | 'approve' | 'approved'>,

  vouchers: {
    area: 'Issues', title: 'Issue Vouchers', basePath: '/stock/issues/vouchers',
    description: 'Stock issued against approved requisitions and acknowledged by the person who received it.',
    views: [
      { key: 'issue', label: 'Issue' },
      { key: 'awaiting', label: 'Acknowledgement' },
      { key: 'returns', label: 'Returns to store' },
    ],
    render: (view, ctx) => {
      switch (view) {
        case 'awaiting': return <AwaitingTab ctx={ctx} />;
        case 'returns': return <ReturnsTab ctx={ctx} />;
        default: return <IssueTab ctx={ctx} />;
      }
    },
  } satisfies StockSectionDef<'issue' | 'awaiting' | 'returns'>,

  transfers: {
    area: 'Transfers', title: 'Transfers', basePath: '/stock/transfers/transfers',
    description: 'Two-step transfers between stores: picked and packed, dispatched into transit, received blind, every difference explained.',
    views: [
      { key: 'draft', label: 'Draft' },
      { key: 'transit', label: 'In transit' },
      { key: 'receive', label: 'To receive' },
      { key: 'held', label: 'Held' },
      { key: 'closed', label: 'Closed' },
    ],
    render: (view, ctx) => <TransfersTab ctx={ctx} view={view} />,
  } satisfies StockSectionDef<'draft' | 'transit' | 'receive' | 'held' | 'closed'>,

  discrepancies: {
    area: 'Transfers', title: 'Differences', basePath: '/stock/transfers/discrepancies',
    description: 'Shortages, damage, broken seals and weight differences found on receipt, each explained and approved before the transfer closes.',
    views: [{ key: 'open', label: 'Differences' }],
    render: (_view, ctx) => <DiscrepanciesTab ctx={ctx} />,
  } satisfies StockSectionDef<'open'>,

  controlledRegister: {
    area: 'Transfers', title: 'Controlled Items Register', basePath: '/stock/transfers/controlled-register',
    description: 'The append-only register of controlled items, two people on every movement.',
    views: [{ key: 'register', label: 'Register' }],
    render: () => <ControlledRegisterTab />,
  } satisfies StockSectionDef<'register'>,

  counts: {
    area: 'Counts', title: 'Stock Counts', basePath: '/stock/counts/counts',
    description: 'Scheduled and ad hoc counts: frozen, counted blind, recounted where over tolerance, reviewed into an adjustment.',
    views: [
      { key: 'schedules', label: 'Schedules' },
      { key: 'planned', label: 'Planned' },
      { key: 'counting', label: 'Counting' },
      { key: 'recount', label: 'Recount' },
      { key: 'review', label: 'Review' },
      { key: 'posted', label: 'Posted' },
    ],
    render: (view, ctx) => {
      switch (view) {
        case 'schedules': return <SchedulesTab ctx={ctx} />;
        case 'posted': return <PostedCountsTab />;
        default: return <CountsListTab ctx={ctx} view={view} />;
      }
    },
  } satisfies StockSectionDef<'schedules' | 'planned' | 'counting' | 'recount' | 'review' | 'posted'>,

  adjustments: {
    area: 'Adjustments', title: 'Adjustments & Write-offs', basePath: '/stock/adjustments/adjustments',
    description: 'Corrections and losses, each with a reason, a comment and evidence where the reason demands it.',
    views: [
      { key: 'adjustments', label: 'Adjustments' },
      { key: 'writeoffs', label: 'Write-offs' },
      { key: 'approve', label: 'To approve' },
      { key: 'posted', label: 'Posted' },
      { key: 'reasons', label: 'Reason accounts' },
    ],
    render: (view, ctx) => {
      switch (view) {
        case 'posted': return <PostedAdjustmentsTab />;
        case 'reasons': return <ReasonAccountsTab ctx={ctx} />;
        default: return <AdjustmentsListTab ctx={ctx} view={view} />;
      }
    },
  } satisfies StockSectionDef<'adjustments' | 'writeoffs' | 'approve' | 'posted' | 'reasons'>,

  recalls: {
    area: 'Adjustments', title: 'Status, Recalls & Destruction', basePath: '/stock/adjustments/recalls',
    description: 'What state stock is in, batches frozen everywhere in one action, and witnessed destruction of what cannot be used.',
    views: [
      { key: 'holdings', label: 'Stock by status' },
      { key: 'recalls', label: 'Recalls' },
      { key: 'destructions', label: 'Destruction' },
    ],
    render: (view, ctx) => {
      switch (view) {
        case 'recalls': return <RecallsListTab />;
        case 'destructions': return <DestructionsTab />;
        default: return <HoldingsTab ctx={ctx} />;
      }
    },
  } satisfies StockSectionDef<'holdings' | 'recalls' | 'destructions'>,

  reorder: {
    area: 'Adjustments', title: 'Reorder', basePath: '/stock/adjustments/reorder',
    description: 'What is below its level once reservations, open orders and pending issues are counted, and one step to a purchase requisition.',
    views: [{ key: 'report', label: 'Reorder report' }],
    render: (_view, ctx) => <ReorderTab ctx={ctx} />,
  } satisfies StockSectionDef<'report'>,

  supply: {
    area: 'Adjustments', title: 'Supply', basePath: '/stock/adjustments/supply',
    description: 'What use says about each store: how long stock will last, what will expire first and where it could go, how suppliers have done, and store temperatures.',
    views: [{ key: 'forecast', label: 'Forecast' }, { key: 'expiring', label: 'Expiring' }, { key: 'suppliers', label: 'Supplier performance' }, { key: 'temperature', label: 'Temperature' }],
    render: (view, ctx) => view === 'expiring' ? <ExpiringTab /> : view === 'suppliers' ? <SupplierPerformanceTab /> : view === 'temperature' ? <TemperatureTab canConfigure={ctx.canConfigure} canReceive={ctx.canReceive} /> : <ForecastTab canConfigure={ctx.canConfigure} />,
  } satisfies StockSectionDef<'forecast' | 'expiring' | 'suppliers' | 'temperature'>,

  reports: {
    area: 'Stock', title: 'Reports', basePath: '/stock/reports',
    description: 'Every stock report, each line drilling to the movement behind it, and each exportable to Excel or PDF.',
    views: [
      { key: 'balances', label: 'On hand & bin cards' },
      { key: 'on-hand', label: 'On hand' },
      { key: 'movements', label: 'Movements' },
      { key: 'valuation', label: 'Valuation vs ledger' },
      { key: 'expiry', label: 'Expiry' },
      { key: 'ageing', label: 'Ageing' },
      { key: 'slow-moving', label: 'Slow moving' },
      { key: 'issues', label: 'Issues by cost centre' },
      { key: 'grni', label: 'Received not invoiced' },
      { key: 'in-transit', label: 'In transit' },
      { key: 'transfers', label: 'Transfer variance' },
      { key: 'counts', label: 'Count variances' },
      { key: 'write-offs', label: 'Write-offs' },
      { key: 'controlled', label: 'Controlled register' },
      { key: 'temperature', label: 'Temperature' },
      { key: 'gross-profit', label: 'Gross profit' },
    ],
    render: (view) => (view === 'balances' ? <BalancesTab /> : <StockReportsTab view={view} />),
  } satisfies StockSectionDef<'balances' | 'on-hand' | 'movements' | 'valuation' | 'expiry' | 'ageing' | 'slow-moving' | 'issues' | 'grni' | 'in-transit' | 'transfers' | 'counts' | 'write-offs' | 'controlled' | 'temperature' | 'gross-profit'>,

  settings: {
    area: 'Stock', title: 'Stock Settings', basePath: '/stock/settings',
    description: 'Costing, tolerances, the accounts every movement posts to, the document numbers, and the stores close.',
    views: [
      { key: 'general', label: 'Settings' },
      { key: 'accounts', label: 'Accounts' },
      { key: 'numbering', label: 'Numbering' },
      { key: 'close', label: 'Stores close' },
    ],
    render: (view, ctx) => {
      switch (view) {
        case 'accounts': return <SettingsAccountsTab ctx={ctx} />;
        case 'numbering': return <SettingsNumberingTab />;
        case 'close': return <SettingsCloseTab ctx={ctx} />;
        default: return <SettingsGeneralTab ctx={ctx} />;
      }
    },
  } satisfies StockSectionDef<'general' | 'accounts' | 'numbering' | 'close'>,
};
